/**
 * Multicolumn - Mobile dots pagination
 */

class MulticolumnDots {
  constructor(section) {
    this.section = section;
    // The scrollable container is .swipe-mobile (which is .section__content)
    this.scrollContainer = section.querySelector('.swipe-mobile');
    // The inner grid container
    this.inner = section.querySelector('.swipe-mobile__inner');
    this.dotsContainer = section.querySelector('[data-dots-container]');
    
    // Check which section type to determine dot class
    const isBiomarkers = section.classList.contains('multicolumn-biomarkers');
    const dotClass = isBiomarkers ? '.multicolumn-biomarkers__dot' : '.multicolumn__dot';
    this.dots = this.dotsContainer?.querySelectorAll(dotClass);

    if (!this.dotsContainer || !this.scrollContainer || !this.dots?.length) return;

    this.mediaQuery = window.matchMedia('(max-width: 767.98px)');

    if (this.mediaQuery.matches) {
      this.init();
    }

    this.mediaQueryHandler = (e) => {
      if (e.matches) {
        this.init();
      } else {
        this.disable();
      }
    };
    this.mediaQuery.addEventListener('change', this.mediaQueryHandler);
  }

  init() {
    if (!this.scrollContainer || !this.dots || !this.inner) return;

    // Get slides from the inner container
    this.slides = this.inner.querySelectorAll('.f-column');
    if (!this.slides.length) return;

    // Wait for layout to be ready before calculating
    setTimeout(() => {
      this.updateSlideWidth();
      this.updateActiveDot();
    }, 100);

    this.scrollHandler = () => {
      window.requestAnimationFrame(() => this.updateActiveDot());
    };
    // Listen to scroll on the swipe-mobile container (the actual scrolling element)
    this.scrollContainer.addEventListener('scroll', this.scrollHandler, { passive: true });

    this.dots.forEach((dot, index) => {
      dot.addEventListener('click', () => this.scrollToSlide(index));
    });

    // Initial update after a short delay to ensure layout is ready
    setTimeout(() => {
      this.updateActiveDot();
    }, 150);

    this.resizeObserver = new ResizeObserver(() => {
      setTimeout(() => {
        this.updateSlideWidth();
        this.updateActiveDot();
      }, 50);
    });
    this.resizeObserver.observe(this.scrollContainer);
  }

  updateSlideWidth() {
    if (this.slides && this.slides.length > 0 && this.inner && this.scrollContainer) {
      // For swipe-mobile, calculate based on actual scrollable width
      // Account for CSS variables and gaps
      const firstSlide = this.slides[0];
      const styles = window.getComputedStyle(this.inner);
      const gap =
        parseFloat(styles.columnGap || '0') ||
        parseFloat(styles.gap || '0') ||
        0;

      // Get the actual width including gap
      this.slideWidth = firstSlide.offsetWidth + gap;
      
      // Alternative: calculate from scroll width if available
      if (this.scrollContainer.scrollWidth > 0 && this.slides.length > 0) {
        const totalScrollWidth = this.scrollContainer.scrollWidth;
        const calculatedWidth = totalScrollWidth / this.slides.length;
        // Use the more accurate calculation
        if (calculatedWidth > 0) {
          this.slideWidth = calculatedWidth;
        }
      }
    }
  }

  updateActiveDot() {
    if (!this.scrollContainer || !this.dots || !this.slides) return;
    
    // Recalculate slide width if not set
    if (!this.slideWidth) {
      this.updateSlideWidth();
    }
    
    const scrollLeft = this.scrollContainer.scrollLeft;
    const scrollWidth = this.scrollContainer.scrollWidth;
    const clientWidth = this.scrollContainer.clientWidth;
    
    // Find which slide is currently most visible
    let activeIndex = 0;
    let minDistance = Infinity;
    
    this.slides.forEach((slide, index) => {
      const slideRect = slide.getBoundingClientRect();
      const containerRect = this.scrollContainer.getBoundingClientRect();
      
      // Calculate how centered the slide is in the viewport
      const slideCenter = slideRect.left + slideRect.width / 2;
      const containerCenter = containerRect.left + containerRect.width / 2;
      const distance = Math.abs(slideCenter - containerCenter);
      
      if (distance < minDistance) {
        minDistance = distance;
        activeIndex = index;
      }
    });
    
    // Ensure index is within bounds
    activeIndex = Math.min(Math.max(0, activeIndex), this.dots.length - 1);

    const isBiomarkers = this.section.classList.contains('multicolumn-biomarkers');
    const activeClass = isBiomarkers ? 'multicolumn-biomarkers__dot--active' : 'multicolumn__dot--active';
    
    this.dots.forEach((dot, index) => {
      dot.classList.toggle(activeClass, index === activeIndex);
    });
  }

  scrollToSlide(index) {
    if (!this.scrollContainer || index < 0 || index >= this.slides.length) return;
    
    // Recalculate if needed
    if (!this.slideWidth) {
      this.updateSlideWidth();
    }
    
    if (!this.slideWidth) {
      // Fallback: scroll to the slide element directly
      const targetSlide = this.slides[index];
      if (targetSlide) {
        targetSlide.scrollIntoView({
          behavior: 'smooth',
          block: 'nearest',
          inline: 'start'
        });
      }
      return;
    }

    const targetScrollLeft = index * this.slideWidth;
    this.scrollContainer.scrollTo({
      left: targetScrollLeft,
      behavior: 'smooth',
    });
  }

  disable() {
    if (this.scrollContainer && this.scrollHandler) {
      this.scrollContainer.removeEventListener('scroll', this.scrollHandler);
    }
    if (this.resizeObserver) {
      this.resizeObserver.disconnect();
    }
  }
}

function initMulticolumnDots() {
  // Handle both multicolumn and multicolumn-biomarkers sections
  document.querySelectorAll('.section.multicolumn, .section.multicolumn-biomarkers').forEach((section) => {
    if (!section.dataset.multicolumnDotsInitialized) {
      section.dataset.multicolumnDotsInitialized = 'true';
      // Only init if dots markup exists
      if (section.querySelector('[data-dots-container]')) {
        new MulticolumnDots(section);
      }
    }
  });
}

if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', initMulticolumnDots);
} else {
  initMulticolumnDots();
}

document.addEventListener('shopify:section:load', (event) => {
  const section = event.target.querySelector('.section.multicolumn, .section.multicolumn-biomarkers');
  if (section) {
    section.dataset.multicolumnDotsInitialized = 'false';
    initMulticolumnDots();
  }
});

